'use client';

import { HoverPrefetchLink } from '@/components/ui/hover-prefetch-link';
import { usePathname } from 'next/navigation';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from '@/components/ui/sidebar';
import { IconMapper } from '@/components/ui/icon-mapper';
import { useTodayTasks } from '@/lib/services/tasks';
import { useFeatureFlags } from '@/lib/providers/feature-flags-provider';
import {
  getVisibleNavItems,
  type NavItem,
  type NavSection
} from '@/components/app-sidebar/navigation-data';

// Selection (active) style for the main nav (LF-4666 Phase 1, corrected in
// LF-4774, softened in LF-4796, weight corrected in LF-4799): a subtle
// neutral gray fill that sits just below the lighter hover gray, with the
// label in full ink + medium weight. Intentionally on-brand — no green tint or
// indicator bar. Layered on top of the shared SidebarMenuButton variants via
// className so the matter/account sidebars, which reuse the same primitive,
// are left untouched.
//
// `--surface-selected` is the token that HOLDS that "just below hover"
// relationship (VALE-535). It must not be swapped for `--muted`/`--secondary`
// again: all three of those are one value in light (0.9702), and `--accent` is
// what SidebarMenuButton paints on hover — so `bg-muted` here renders the
// selected row and a hovered row as the same pixel and the sidebar stops
// saying which page you are on. `__tests__/nav-active-vs-hover-distinct.test.ts`
// reads this very string and fails if the two ever resolve equal.
const NAV_SELECTION_CLASS =
  'data-[active=true]:bg-surface-selected data-[active=true]:font-medium data-[active=true]:text-sidebar-foreground';

export function MainSidebarContent({ items = [] }: { items: NavSection[] }) {
  const { features } = useFeatureFlags();
  const pathname = usePathname();
  const { setOpenMobile, isMobile } = useSidebar();

  // Fetch today tasks count for the badge — gated by today-upcoming flag,
  // since the Today nav item (and its badge) are only shown when enabled.
  const todayUpcomingEnabled = features.includes('today-upcoming');
  const { data: todayTasks } = useTodayTasks(undefined, undefined, undefined, {
    enabled: todayUpcomingEnabled
  });
  const todayCount = todayTasks?.length || 0;

  const handleLinkClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // Active when the route is the nav target OR a child of it, so sub-routes
  // (e.g. /intake/new, /intake/:id, /scribe/:id) keep their top-level item lit.
  // The trailing-slash boundary stops a false prefix match (e.g. /all vs
  // /allocations); the '#' section-header url is never a real destination.
  // Root ('/') renders the same All-Matters view as '/all' (LF-3059), so it
  // must also light up the Matters item (LF-4797).
  const isNavActive = (url: string) =>
    url !== '#' &&
    (pathname === url ||
      pathname.startsWith(`${url}/`) ||
      (pathname === '/' && url === '/all'));

  const iconClass = (id: string | undefined, active: boolean) => {
    if (id === 'today') {
      return 'fill-warning text-warning';
    }
    return active ? 'text-foreground-secondary' : 'text-muted-foreground';
  };

  const renderIcon = (subItem: NavItem, active: boolean) => {
    const color = iconClass(subItem.id, active);
    if (!subItem.icon) {
      return null;
    }
    return <IconMapper iconName={subItem.icon} className={color} />;
  };

  const renderItem = (subItem: NavItem) => {
    const active = isNavActive(subItem.url);

    return (
      <SidebarMenuItem key={subItem.title}>
        <SidebarMenuButton
          asChild
          tooltip={subItem.title}
          isActive={active}
          className={NAV_SELECTION_CLASS}
        >
          <HoverPrefetchLink href={subItem.url} onClick={handleLinkClick}>
            {renderIcon(subItem, active)}
            <span>{subItem.title}</span>
            {subItem.id === 'today' && todayCount > 0 && (
              <span className="text-muted-foreground ml-auto text-xs font-semibold">
                {todayCount}
              </span>
            )}
          </HoverPrefetchLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  // Flat, ungrouped list (LF-4815 — reverts LF-4796's labelled sections):
  // sections still carry their own feature-flag gate, but no header renders
  // and every visible item across all sections lands in one SidebarMenu. The
  // flatten + gate is shared with the compact icon rail so the two cannot drift
  // (VALE-1971).
  const visibleItems = getVisibleNavItems(items, features);

  return (
    <SidebarMenu>{visibleItems.map((item) => renderItem(item))}</SidebarMenu>
  );
}
